import React, { createContext, useContext } from 'react';

import { EMPTY_VALUE, formatNumber } from '../lib/formatters';
import { EYEBROW, cn } from '../lib/utils';

type PropertyListElement = 'dl' | 'div';

const PropertyListContext = createContext<PropertyListElement>('dl');

interface PropertyListProps {
  children: React.ReactNode;
  /**
   * `default` is the one 9.5rem label column every record dialog shares.
   * `auto` sizes the column to its labels — only for a narrow inspector.
   */
  labelWidth?: 'default' | 'auto';
  /**
   * `dl` (default) for read-only facts. `div` when the list also hosts
   * `Field orientation="horizontal"` rows: a <dl> may hold only dt/dd groups,
   * and a Field renders a <label> and a <div>.
   */
  as?: PropertyListElement;
  className?: string;
}

/**
 * Read-only facts about one record, as a two-column label/value grid
 * (DESIGN_SYSTEM.md §4). The grid classes live here and nowhere else, so every
 * record dialog shares one scan path. `Field orientation="horizontal"` rows
 * placed inside (with `as="div"`) land in the same columns.
 */
export const PropertyList: React.FC<PropertyListProps> = ({
  children,
  labelWidth = 'default',
  as = 'dl',
  className,
}) => {
  const Element = as;
  return (
    <PropertyListContext.Provider value={as}>
      <Element
        className={cn(
          'grid items-baseline gap-x-4 gap-y-2 text-sm',
          labelWidth === 'auto' ? 'grid-cols-[auto_minmax(0,1fr)]' : 'grid-cols-[9.5rem_minmax(0,1fr)]',
          className,
        )}
        data-property-list=""
      >
        {children}
      </Element>
    </PropertyListContext.Provider>
  );
};

const isAbsent = (value: React.ReactNode): boolean =>
  value === null || value === undefined || value === '' || value === false;

interface PropertyRowProps {
  label: string;
  /** Mono for machine-read values: slugs, digests, coordinates, ids. */
  mono?: boolean;
  /** Absent (null / undefined / '') renders EMPTY_VALUE. */
  children?: React.ReactNode;
  className?: string;
}

/**
 * One label/value pair of a `PropertyList`. No copy button: an id someone needs
 * to copy is a `CredentialField`.
 */
export const PropertyRow: React.FC<PropertyRowProps> = ({ label, mono = false, children, className }) => {
  const as = useContext(PropertyListContext);
  const Term = as === 'dl' ? 'dt' : 'span';
  const Detail = as === 'dl' ? 'dd' : 'span';
  const absent = isAbsent(children);
  return (
    <div className={cn('contents', className)}>
      <Term className="text-muted-foreground">{label}</Term>
      <Detail className={cn('min-w-0 break-words', mono && !absent && 'font-mono text-xs')}>
        {absent ? EMPTY_VALUE : children}
      </Detail>
    </div>
  );
};

interface SectionHeadingProps {
  children: React.ReactNode;
  /** "Members · 2". null / undefined shows no count (unknown is not zero). */
  count?: number | null;
  /** A right-aligned slot (a small button that acts on the section). */
  action?: React.ReactNode;
  /** For `aria-labelledby` on the section it heads. */
  id?: string;
  className?: string;
}

/**
 * A dialog section's heading: a real h3 in the eyebrow style, so screen-reader
 * users can jump between sections. A section holding one self-labelled control
 * takes no heading.
 */
export const SectionHeading: React.FC<SectionHeadingProps> = ({ children, count, action, id, className }) => (
  <div className={cn('flex items-center justify-between gap-2', className)}>
    <h3 id={id} className={EYEBROW}>
      {children}
      {count !== null && count !== undefined ? ` · ${formatNumber(count)}` : null}
    </h3>
    {action}
  </div>
);
